import type { TranscriptDoc } from '../utils/transcriptDoc'

export type DeliverableType =
  | 'Video'
  | 'Audio'
  | 'Photography'
  | 'Document'
  | 'Graphics'
  | 'Transcript'
  | 'Folder'
  | 'Other'

export const DELIVERABLE_TYPES: DeliverableType[] = [
  'Video',
  'Audio',
  'Photography',
  'Document',
  'Graphics',
  'Transcript',
  'Folder',
  'Other',
]

/**
 * How a video deliverable is framed, so the client player uses the right
 * container. Only applies to the Video type; anything without one is landscape.
 */
export type VideoFormat = '16:9 Landscape' | '9:16 Vertical'

export const VIDEO_FORMATS: VideoFormat[] = ['16:9 Landscape', '9:16 Vertical']

/**
 * Where a deliverable sits in the production process. Never set by hand — it is
 * derived from the deliverable's status, so the two can never contradict.
 * See `deliverableStage` in utils/deliverables.ts.
 */
export type DeliverableStage = 'Pre-Production' | 'Production' | 'Review' | 'Final Delivery'

export const DELIVERABLE_STAGES: DeliverableStage[] = [
  'Pre-Production',
  'Production',
  'Review',
  'Final Delivery',
]

/**
 * The smallest workflow that describes real production. Seven states with no
 * overlap: In Progress is the shoot itself, Editing is post-production once
 * filming wraps, Approved means the client signed it off, Delivered means the
 * finals are with them and the deliverable is closed.
 *
 * Earlier values (Not Started, In Production, Ready for Download) are mapped
 * forward on load by `normaliseStatus`, so stored data stays valid.
 */
export type DeliverableStatus =
  | 'Planned'
  | 'In Progress'
  | 'Editing'
  | 'Ready for Review'
  | 'Changes Requested'
  | 'Approved'
  | 'Delivered'

export const DELIVERABLE_STATUSES: DeliverableStatus[] = [
  'Planned',
  'In Progress',
  'Editing',
  'Ready for Review',
  'Changes Requested',
  'Approved',
  'Delivered',
]

/** Statuses where the client is expected to watch and respond. */
export const REVIEWABLE: DeliverableStatus[] = ['Ready for Review']

export type NoteAuthor = 'Client' | 'Portal' | 'System'

export interface Note {
  id: string
  author: NoteAuthor
  /** The named person who left it, e.g. "Sarah Jones". */
  authorName?: string
  /** Position in the video the note refers to, e.g. "00:17". */
  timecode?: string
  /** Clock time the note was submitted — secondary metadata. */
  time: string
  body: string
}

export interface TeamMember {
  id: string
  name: string
  role: string
}

export interface Activity {
  id: string
  projectId: string
  projectName: string
  actorName: string
  /** e.g. "requested changes to Smart Cities" */
  action: string
  /** Set for general Contact Portal messages, kept separate from review notes. */
  message?: string
  deliverableId: string
  at: number
}

export interface ClientAlert {
  id: string
  projectId: string
  kind: 'review' | 'files'
  title: string
  body: string
  actionLabel: string
  deliverableId: string
}

export interface ChangeRequest {
  id: string
  body: string
  /** Position in the video the change refers to, e.g. "00:17". */
  timecode?: string
  /** The client user who submitted it. */
  authorName: string
  /** Clock time submitted — secondary metadata. */
  time: string
  submitted: string
  /** Epoch ms it was raised, so waiting time can be calculated. */
  at?: number
  /** The review version this change was raised against. */
  version: string
  resolved: boolean
  /** True when this request exists in the shared database, not just locally. */
  shared?: boolean
  /** When Portal marked it completed, e.g. "16:18". */
  completedAt?: string
  /** The Portal staff member who completed it. */
  completedBy?: string
}

export interface VersionEntry {
  id: string
  version: string
  /** e.g. "First Cut", "Changes Requested", "Reviewed". */
  label: string
  date: string
  driveUrl: string
  /** How many changes the client raised against this version. */
  changeCount?: number
  /** Older versions stay internal unless Portal switches this on. */
  visibleToClient: boolean
}

/** A saved state of a transcript. The original upload is always the first one. */
export interface TranscriptRevision {
  id: string
  text: string
  /** The structured document, carrying this state's tracked deletions. */
  doc?: TranscriptDoc
  /** Who saved it — a client name, or "Portal" for the original upload. */
  editorName: string
  /** Epoch ms, so it can be formatted in the viewer's own timezone. */
  at: number
}

export interface Deliverable {
  id: string
  name: string
  type: DeliverableType
  /** Legacy count. Retained for stored data only — no longer edited or shown. */
  quantity: string
  /** The only progress field staff set. The stage follows from it. */
  status: DeliverableStatus
  /** Optional, e.g. "V3" or "FINAL". */
  version: string
  versionDate: string
  runtime: string
  /** Google Drive video/file/folder link. */
  driveUrl: string
  /** Optional Google Drive audio review link. */
  audioUrl: string
  /** Video framing. Absent means 16:9 Landscape, so existing items are safe. */
  videoFormat?: VideoFormat
  /** Legacy internal note. Retained for stored data only — no longer edited. */
  internalNote: string
  /** Transcript text, for Transcript items. Uploaded by Portal, editable by the client. */
  transcript?: string
  /** Every saved state, oldest first. Nothing is ever overwritten. */
  transcriptRevisions?: TranscriptRevision[]
  /** The source document Portal uploaded, e.g. "Premier League Picks.docx". Staff only. */
  transcriptFilename?: string
  /** Word count of that upload. Staff only. */
  transcriptWordCount?: number
  visibleToClient: boolean
  notes: Note[]
  changeRequests: ChangeRequest[]
  /** Superseded versions, newest first. */
  versions: VersionEntry[]
  /** The client who approved the current version, where one has. */
  approvedBy?: string
  /** Epoch ms of that approval, so waiting time can be calculated. */
  approvedAt?: number
}

export interface ShootDetail {
  label: string
  value: string
}

export interface Shoot {
  id: string
  /** ISO date, e.g. "2026-09-25". */
  date: string
  title: string
  time: string
  city: string
  location: string
  details: ShootDetail[]
}

/** A PDF call sheet belonging to the scheduled shoot day. */
export interface CallSheet {
  fileName: string
  /** Public, permanent link — resolved from `path`, never a browser blob URL. */
  url: string
  /** The Supabase Storage object path. Needed to replace or remove the file. */
  path: string
}

export interface Project {
  id: string
  /** Permanent client link slug, e.g. "bloomberg-economy-of-scale". */
  slug: string
  client: string
  name: string
  type: string
  description: string
  /** Manual note only — the displayed status is derived from the deliverables. */
  status: string
  /** ISO date, e.g. "2026-09-25". Empty when not yet confirmed. */
  nextShootDate: string
  nextShootTime: string
  /** Optional PDF call sheet for the next shoot. */
  callSheet?: CallSheet
  location: string
  clientContact: string
  producer: string
  updated: string
  deliverables: Deliverable[]
  shoots: Shoot[]
  team: TeamMember[]
  archived: boolean
}

export interface TemplateDeliverable {
  name: string
  type: DeliverableType
  status: DeliverableStatus
  visibleToClient: boolean
}

export interface ProjectTemplate {
  id: string
  name: string
  projectType: string
  deliverables: TemplateDeliverable[]
}

export interface FileRow {
  id: string
  label: string
  meta: string
  action: 'Download' | 'Open folder' | 'Open'
  /** Set when the row opens inside Portal rather than in Drive, e.g. a transcript. */
  opensInPortal?: boolean
}
